/**
 * Validation utilities for debt calculator inputs
 * Constitutional requirement: Verbose console logging for all validation errors
 */

//console.log('📋 Loading validation utilities...')

// Input validation ranges from clarifications
const VALIDATION_RANGES = {
  balance: { min: 1, max: 1000000 },
  interestRate: { min: 0, max: 100 },
  monthlyPayment: { min: 1, max: 100000 },
  targetMonths: { min: 1, max: 1200 } // 100 years max
}

/**
 * Validates a numeric input against defined ranges
 * @param {string} fieldName - Name of the field being validated
 * @param {number} value - Value to validate
 * @param {Object} context - Additional context for logging
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateNumericField(fieldName, value, context = {}) {
  //console.log(`🔍 Validating ${fieldName}:`, { value, context })

  // Check if value exists
  if (value === null || value === undefined || value === '') {
    let error;
    switch (fieldName) {
      case 'balance':
        error = 'Balance is required'
        break;
      case 'interestRate':
        error = 'Interest rate is required'
        break;
      case 'monthlyPayment':
        error = 'Monthly payment is required'
        break;
      case 'targetMonths':
        error = 'Target months is required'
        break;
      default:
        error = `${fieldName} is required`
    }
    console.log(`❌ Validation failed for ${fieldName}:`, error)
    return { isValid: false, error }
  }
  
  // Convert to number if string
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  
  // Check if it's a valid number
  if (isNaN(numValue)) {
    const error = `${fieldName} must be a valid number`
    console.log(`❌ Validation failed for ${fieldName}:`, error)
    return { isValid: false, error }
  }
  
  // Check if field has validation rules
  const rules = VALIDATION_RANGES[fieldName]
  if (!rules) {
    console.log(`⚠️ No validation rules found for ${fieldName}, allowing value`)
    return { isValid: true, error: null }
  }
  
  // Check minimum value
  if (numValue < rules.min) {
    let error;
    switch (fieldName) {
      case 'balance':
        error = `Balance must be at least $${rules.min.toLocaleString()}`
        break;
      case 'interestRate':
        error = `Interest rate must be at least ${rules.min.toLocaleString()}%`
        break;
      case 'monthlyPayment':
        error = `Monthly payment must be at least $${rules.min.toLocaleString()}`
        break;
      case 'targetMonths':
        error = `Target time must be at least ${rules.min.toLocaleString()} month`
        break;
      default:
        error = 'Input value is too low'
    }
    
    console.log(`❌ Validation failed for ${fieldName}:`, error, { value: numValue, min: rules.min })
    return { isValid: false, error }
  }
  
  // Check maximum value
  if (numValue > rules.max) {
    let error;
    switch (fieldName) {
      case 'balance':
        error = `Balance cannot exceed $${rules.max.toLocaleString()}`
        break;
      case 'interestRate':
        error = `Interest rate cannot exceed ${rules.max.toLocaleString()}%`
        break;
      case 'monthlyPayment':
        error = `Monthly payment cannot exceed $${rules.max.toLocaleString()}`
        break;
      case 'targetMonths':
        error = `Target time cannot exceed ${rules.max.toLocaleString()} total months (${(rules.max / 12).toLocaleString()} years)`
        break;
      default:
        error = 'Input value is too high'
    }
    console.log(`❌ Validation failed for ${fieldName}:`, error, { value: numValue, max: rules.max })
    return { isValid: false, error }
  }
  
  //console.log(`✅ Validation passed for ${fieldName}:`, numValue)
  return { isValid: true, error: null }
}

/**
 * Validates all debt calculation inputs
 * @param {Object} debtData - Object containing all form inputs
 * @param {string} calculationMode - 'payoffTime' or 'monthlyPayment'
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export function validateDebtCalculation(debtData, calculationMode) {
  console.log('🔍 Validating debt calculation:', { debtData, calculationMode })
  
  const errors = {}
  let isValid = true
  
  // Always validate balance and interest rate
  const balanceResult = validateNumericField('balance', debtData.balance)
  if (!balanceResult.isValid) {
    errors.balance = balanceResult.error
    isValid = false
  }
  
  const interestResult = validateNumericField('interestRate', debtData.interestRate)
  if (!interestResult.isValid) {
    errors.interestRate = interestResult.error
    isValid = false
  }
  
  // Validate mode-specific fields
  if (calculationMode === 'payoffTime') {
    const paymentResult = validateNumericField('monthlyPayment', debtData.monthlyPayment)
    if (!paymentResult.isValid) {
      errors.monthlyPayment = paymentResult.error
      isValid = false
    }
  } else if (calculationMode === 'monthlyPayment') {
    const monthsResult = validateNumericField('targetMonths', debtData.targetMonths)
    if (!monthsResult.isValid) {
      errors.targetMonths = monthsResult.error
      isValid = false
    }
  }
  
  if (isValid) {
    //console.log('✅ All validation checks passed for debt calculation')
  } else {
    console.log('❌ Validation failed for debt calculation:', errors)
  }
  
  return { isValid, errors }
}

/**
 * Validates a single form field in real-time
 * @param {string} fieldName - Name of the field
 * @param {any} value - Current field value
 * @param {string} calculationMode - Current calculation mode
 * @returns {string|null} Error message or null if valid
 */
export function validateFieldRealTime(fieldName, value, calculationMode) {
  console.log(`🔄 Real-time validation for ${fieldName}:`, { value, calculationMode })
  
  // Skip validation for fields not required in current mode
  if (calculationMode === 'payoffTime' && fieldName === 'targetMonths') {
    return null
  }
  if (calculationMode === 'monthlyPayment' && fieldName === 'monthlyPayment') {
    return null
  }
  
  const result = validateNumericField(fieldName, value)
  return result.error
}

